// src/main.jsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline } from '@mui/material';

import App from './App';
import { SettingsProvider } from './context/SettingsContext';
import { SearchProvider } from './context/SearchContext';
import { ThemeProvider } from './context/ThemeContext';
import SnackbarProvider from './components/SnackbarProvider';
import { setupGlobalErrorHandlers } from './utils/errorHandling';

// Wrapper component to ensure theme is available for CssBaseline
function ThemedApp() {
  // Note: useTheme can't be called here as we're not yet inside MuiThemeProvider
  // CssBaseline will use the theme from the parent ThemeProvider automatically
  
  return (
    <>
      <CssBaseline />
      <App />
    </>
  );
}

// Fallback environment detection
const detectEnvironment = () => {
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname.includes('demo')) return 'demo';
    if (hostname.includes('asc')) return 'asc';
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';
    return 'cloudflare';
  }
  return 'cloudflare';
};

// Theme is now handled by ThemeProvider context

// Detect the current environment
const currentEnv = detectEnvironment();

// Initialize global error handlers for client-side error logging
setupGlobalErrorHandlers();

// Reload helper for chunk errors in production
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    const msg = String(e?.message || e?.error || '');
    if (/ChunkLoadError|Loading chunk|Importing a module script failed/i.test(msg)) {
      const url = new URL(window.location.href);
      url.searchParams.set('v', String(Date.now()));
      window.location.replace(url.toString());
    }
  });
  window.addEventListener('unhandledrejection', (e) => {
    const msg = String(e?.reason?.message || e?.reason || '');
    if (/ChunkLoadError|Loading chunk|Importing a module script failed/i.test(msg)) {
      const url = new URL(window.location.href);
      url.searchParams.set('v', String(Date.now()));
      window.location.replace(url.toString());
    }
  });
}

// Service worker registration - disabled in development
if ('serviceWorker' in navigator) {
  const isDevelopment =
    currentEnv === 'local' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  if (isDevelopment) {
    // Unregister any existing service workers in development
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister();
      });
    });
  } else {
    // Register service worker for PWA functionality in production
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js')
        .then(registration => {
          // Service worker registered successfully
        })
        .catch(registrationError => {
          // Service worker registration failed
        });
    });
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider>
        <SnackbarProvider>
          <SettingsProvider>
            <SearchProvider>
              <ThemedApp />
            </SearchProvider>
          </SettingsProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);
